import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { aiPodService, type AiPod } from '../services/aiPodService';
import {
  aiPodBillingService,
  formatMoney,
  isLiveStatus,
  planPriceForCycle,
  type BillingCycle,
  type PlanFeaturesResponse,
  type FeatureMeta,
  type PodSnapshot,
  type PodSubscription,
} from '../services/aiPodBillingService';
import type { BillingPlanDto } from '../services/billingService';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import PodBreadcrumb from '../components/ai-pod/PodBreadcrumb';
import { APP_CONFIG } from '../constants';

const INTERVAL_RANK: Record<string, number> = { day: 1, week: 2, month: 3, year: 4 };

function sortedPlans(plans: BillingPlanDto[]): BillingPlanDto[] {
  return [...plans].sort((a, b) => a.amountMinor - b.amountMinor);
}

export function formatFeatureValue(meta: Pick<FeatureMeta, 'type' | 'name' | 'options' | 'unit'>, value: any): string {
  if (meta.type === 'boolean') return value === true || value === 'true' ? meta.name : '';
  if (meta.type === 'limit') {
    if (value === -1 || value === '-1') return `Unlimited ${meta.name.toLowerCase()}`;
    if (Number(value) === 0) return '';
    return `${Number(value).toLocaleString()} ${meta.name.toLowerCase()}`;
  }
  if (meta.type === 'option') {
    const opt = (meta.options ?? []).find((o) => o.value === String(value));
    return opt ? `${meta.name}: ${opt.label}` : `${meta.name}: ${value}`;
  }
  return `${meta.name}: ${value}`;
}

/** A card for a plan the tenant holds that is no longer offered (archived): built from what they bought. */
function planFromSnapshot(snapshot: PodSnapshot): BillingPlanDto {
  const { plan } = snapshot;
  return {
    id: plan.id,
    planKey: plan.planKey,
    billingMode: 'SUBSCRIPTION',
    displayName: plan.displayName,
    description: plan.description,
    amountMinor: plan.amountMinor,
    currency: plan.currency,
    interval: plan.interval,
    intervalCount: 1,
    stripePriceId: null,
    lastSyncedAt: null,
    trialDays: 0,
    pricingIntervals: [{ interval: plan.interval, amountMinor: plan.amountMinor, currency: plan.currency }],
  };
}

/** Feature lines from a frozen snapshot, in the pricing page's order. */
function snapshotFeatureLines(snapshot: PodSnapshot, metas: FeatureMeta[]): string[] {
  const order = new Map(metas.map((m, i) => [m.id, i]));
  return snapshot.features
    .filter((f) => metas.find((m) => m.id === f.featureId)?.showOnPricingPage !== false)
    .sort((a, b) => (order.get(a.featureId) ?? 999) - (order.get(b.featureId) ?? 999))
    .map((f) => {
      const meta = metas.find((m) => m.id === f.featureId);
      return formatFeatureValue({ type: f.type, name: f.name, unit: f.unit, options: meta?.options ?? null }, f.value);
    })
    .filter((line) => !!line);
}

/** Same rule the server applies: longer cycle or higher price → now; otherwise at renewal. */
function changeKind(
  current: { planId: string; interval: string; amountMinor: number },
  target: { planId: string; interval: string; amountMinor: number }
): 'same' | 'upgrade' | 'downgrade' {
  if (current.planId === target.planId && current.interval === target.interval) return 'same';
  if (current.interval !== target.interval) {
    return (INTERVAL_RANK[target.interval] ?? 3) > (INTERVAL_RANK[current.interval] ?? 3) ? 'upgrade' : 'downgrade';
  }
  return target.amountMinor >= current.amountMinor ? 'upgrade' : 'downgrade';
}

const PodPlans: React.FC = () => {
  const { podId } = useParams<{ podId: string }>();
  const navigate = useNavigate();

  const [pod, setPod] = useState<AiPod | null>(null);
  const [plans, setPlans] = useState<BillingPlanDto[]>([]);
  const [planFeaturesData, setPlanFeaturesData] = useState<PlanFeaturesResponse | null>(null);
  const [subscription, setSubscription] = useState<PodSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  useEffect(() => {
    if (!podId) return;
    setLoading(true);
    setFetchError(null);
    aiPodService
      .getAiPodById(podId)
      .then((p) => {
        setPod(p);
        return Promise.all([
          aiPodBillingService.listBillingPlans(podId).then((r) => setPlans(r.plans || [])).catch(() => setPlans([])),
          aiPodBillingService.getPlanFeaturesPublic(podId).then(setPlanFeaturesData).catch(() => setPlanFeaturesData(null)),
          aiPodBillingService
            .getSubscription(podId)
            .then((s) => {
              setSubscription(s);
              // Open on the cycle the tenant is billed on.
              if (s && isLiveStatus(s.status) && s.snapshot?.plan?.interval === 'year') setBillingCycle('yearly');
            })
            .catch(() => setSubscription(null)),
        ]);
      })
      .catch((err: unknown) => setFetchError(err instanceof Error ? err.message : 'Failed to load pod'))
      .finally(() => setLoading(false));
  }, [podId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-agentic-gray-50">
        <span className="loading-spinner" />
      </div>
    );
  }

  if (fetchError || !pod) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-agentic-gray-50">
        <p className="text-lg font-semibold text-agentic-text-primary">{fetchError ?? 'Pod not found'}</p>
        <Link to="/agent-types?tab=pods" className="text-sm text-agentic-primary-blue underline">
          Back to {APP_CONFIG.PODS_TAB_LABEL}
        </Link>
      </div>
    );
  }

  const live = subscription && isLiveStatus(subscription.status) ? subscription : null;
  const current = live
    ? { planId: live.snapshot.plan.id, interval: live.snapshot.plan.interval, amountMinor: live.snapshot.plan.amountMinor }
    : null;
  const trialAvailable = !subscription?.trialUsedAt;
  // The tenant's own plan always has a card, even once it's no longer offered.
  const ordered = sortedPlans(live && !plans.some((p) => p.id === live.snapshot.plan.id) ? [...plans, planFromSnapshot(live.snapshot)] : plans);
  const hasYearly = ordered.some((p) => planPriceForCycle(p, 'yearly'));
  const hasMonthly = ordered.some((p) => planPriceForCycle(p, 'monthly'));
  const effectiveCycle: BillingCycle = hasMonthly && hasYearly ? billingCycle : hasYearly ? 'yearly' : 'monthly';
  const featureMetas = (planFeaturesData?.features ?? []).filter((f) => f.showOnPricingPage !== false);

  return (
    <div className="min-h-screen bg-agentic-gray-50">
      <div className="mx-auto max-w-5xl px-4 py-10">
        <PodBreadcrumb
          items={[
            { label: pod.name, to: live ? `/pods/${pod.id}/manage` : `/pods/${pod.id}` },
            { label: live ? 'Change plan' : 'Plans' },
          ]}
        />

        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-kv-agentic-body">{live ? 'Change your plan' : 'Choose a plan'}</h1>
            <p className="mt-1.5 text-sm text-agentic-text-secondary">
              {live
                ? 'Upgrades apply right away. Downgrades and moves to monthly billing apply at your next renewal.'
                : 'Your plan starts immediately (after any free trial). Cancel anytime.'}
            </p>
          </div>
          {hasMonthly && hasYearly && (
            <div className="flex items-center gap-2 text-sm">
              {(['monthly', 'yearly'] as BillingCycle[]).map((cycle) => (
                <Button
                  key={cycle}
                  tone="custom"
                  onClick={() => setBillingCycle(cycle)}
                  className={`rounded-xl px-5 py-2.5 font-semibold capitalize transition-all border ${
                    billingCycle === cycle
                      ? 'bg-white border-agentic-gray-300 text-agentic-text-primary shadow-sm'
                      : 'bg-transparent border-transparent text-agentic-text-muted hover:text-agentic-text-secondary'
                  }`}
                >
                  {cycle}
                </Button>
              ))}
            </div>
          )}
        </div>

        {ordered.length === 0 ? (
          <div className="rounded-panel bg-white p-10 text-center text-agentic-text-secondary">No plans available for this pod yet.</div>
        ) : (
          <div className="pt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {ordered.map((plan) => {
              const cycleInterval = effectiveCycle === 'yearly' ? 'year' : 'month';
              // The plan the tenant is on, and one scheduled to start at renewal, are shown as they
              // were bought (frozen), not as the plan reads today.
              const frozen: PodSnapshot | null =
                live && plan.id === live.snapshot.plan.id && live.snapshot.plan.interval === cycleInterval
                  ? live.snapshot
                  : live?.pendingSnapshot && plan.id === live.pendingPlanId && live.pendingInterval === cycleInterval
                    ? live.pendingSnapshot
                    : null;
              // The real price for the selected cycle; null when this plan isn't sold on it.
              const offered = planPriceForCycle(plan, effectiveCycle);
              const price = frozen
                ? { interval: cycleInterval as 'month' | 'year', amountMinor: frozen.plan.amountMinor, currency: frozen.plan.currency }
                : offered;
              const isFree = price?.amountMinor === 0;
              const kind =
                current && price ? changeKind(current, { planId: plan.id, interval: price.interval, amountMinor: price.amountMinor }) : null;
              const isCurrent = kind === 'same';
              const isPending = !!live?.pendingPlanId && live.pendingPlanId === plan.id && live.pendingInterval === price?.interval;
              const trialDays = !live && trialAvailable && !isFree ? plan.trialDays ?? 0 : 0;

              const planWithFeatures = planFeaturesData?.plans.find((p) => p.id === plan.id);
              const offeredLines = featureMetas
                .map((meta) => {
                  // Values are per billing period: show the ones for the cycle being viewed.
                  const pf = planWithFeatures?.planFeatures.find((x) => x.featureId === meta.id && x.interval === cycleInterval);
                  return formatFeatureValue(meta, pf ? pf.value : meta.defaultValue);
                })
                .filter((line) => !!line);
              const featureLines = frozen ? snapshotFeatureLines(frozen, featureMetas) : offeredLines;
              const stillOffered = plans.some((p) => p.id === plan.id);
              // What new subscribers get differs from the terms this tenant holds.
              const changedSince =
                !!frozen &&
                stillOffered &&
                (offered?.amountMinor !== frozen.plan.amountMinor ||
                  plan.displayName !== frozen.plan.displayName ||
                  offeredLines.join('|') !== featureLines.join('|'));
              const displayName = frozen?.plan.displayName ?? plan.displayName;
              const description = frozen ? frozen.plan.description : plan.description;

              let cta = 'Get started';
              if (isFree) cta = live ? 'Switch to free plan' : 'Activate for free';
              if (trialDays > 0) cta = `Start ${trialDays}-day free trial`;
              // Moving off a free plan is an upgrade, whatever the billing cycle.
              if (kind === 'upgrade')
                cta = current && price && current.amountMinor > 0 && current.interval !== price.interval ? `Switch to ${effectiveCycle} billing` : 'Upgrade now';
              if (kind === 'downgrade') cta = 'Switch at next renewal';

              return (
                <div
                  key={plan.id}
                  className={`relative flex flex-col rounded-panel bg-white p-6 ${
                    isCurrent ? 'border-2 border-kv-agentic-blue' : 'border border-agentic-gray-200'
                  }`}
                >
                  {(isCurrent || isPending || trialDays > 0) && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
                      <Badge className="rounded-full bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue px-4 py-1.5 text-xs font-semibold text-white">
                        {isCurrent
                          ? live?.status === 'trialing'
                            ? 'Current plan · Trial'
                            : 'Current plan'
                          : isPending
                            ? 'Starts at renewal'
                            : `${trialDays}-day free trial`}
                      </Badge>
                    </div>
                  )}

                  <h3 className="text-xl font-bold text-agentic-text-primary">{displayName}</h3>
                  <p className="mt-1 min-h-5 text-sm text-agentic-text-secondary">{description || ' '}</p>

                  {price ? (
                    <>
                      <div className="mt-6">
                        {isFree ? (
                          <span className="text-4xl font-bold text-kv-agentic-body">Free</span>
                        ) : (
                          <>
                            <span className="text-4xl font-bold text-kv-agentic-body">{formatMoney(price.amountMinor, price.currency)}</span>
                            <span className="text-sm text-agentic-text-muted"> /{price.interval}</span>
                          </>
                        )}
                      </div>
                      {!isFree && price.interval === 'year' && (
                        <p className="mt-1 text-xs text-agentic-text-muted">
                          {formatMoney(Math.round(price.amountMinor / 12), price.currency)}/month, billed yearly
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-6 text-sm text-agentic-text-muted">Not available with {effectiveCycle} billing.</p>
                  )}

                  {frozen && (changedSince || !stillOffered) && (
                    <p className="mt-3 rounded-lg bg-agentic-gray-50 px-3 py-2 text-xs text-agentic-text-secondary">
                      {!stillOffered
                        ? 'This plan is no longer offered to new subscribers. You keep it as shown.'
                        : `Shown as you ${isCurrent ? 'subscribed' : 'scheduled it'}. This plan has since been updated for new subscribers; your terms don't change.`}
                    </p>
                  )}
                                    {featureLines.length > 0 && (
                    <ul className="mt-4 space-y-2.5">
                      {featureLines.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-agentic-text-primary">
                          <svg className="mt-0.5 h-4 w-4 shrink-0 text-kv-agentic-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto pt-5">
                    {isCurrent ? (
                      <Button variant="ghost" size="md" className="w-full" onClick={() => navigate(`/pods/${pod.id}/manage`)}>
                        Manage subscription
                      </Button>
                    ) : (
                      <Button
                        onClick={() =>
                          navigate(`/pods/${pod.id}/checkout?planId=${plan.id}&cycle=${price?.interval === 'year' ? 'yearly' : 'monthly'}`)
                        }
                        variant={kind === 'downgrade' ? 'ghost' : 'primary'}
                        size="md"
                        className="w-full"
                        disabled={!price || isPending}
                      >
                        {isPending ? 'Scheduled' : cta}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PodPlans;
